import React from "react";
import { IconButton, makeStyles, Tooltip } from "@material-ui/core";
import "./GuardiaVehicleTable.css";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import { blue900, lightGray, white } from "../constants";
import Badge from "../Badge/Badge";
import Button from "../Button/Button";
import VehicleTypeIcon from "./vehicleTypeIcon";
import { formatGuardiaFecha } from "./utils/formatDate";
import { driverName, tipoLabel, VehicleInsideRow } from "./utils/vehicleInsideRow";
import { AsociadaChip } from "./VehicleUnitCells";
import { getEntryTransportAuthorization } from "./utils/entryAuthorization";
import type { EditDockTarget } from "../Dialog/EditAssignedDockDialog";

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: white,
    border: "1px solid #E0E0E0",
    borderRadius: 12,
    marginTop: 12,
    overflow: "hidden",
  },
  /** Mismo aviso que el borde ámbar de la fila de escritorio. */
  rootAlert: {
    boxShadow: "inset 4px 0 0 #FEE685",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: 16,
    borderBottom: "1px solid #E0E0E0",
    color: blue900,
  },
  plate: {
    fontWeight: 700,
    color: blue900,
    fontSize: 18,
    letterSpacing: 0.8,
  },
  tipo: {
    color: lightGray,
    fontSize: 13,
  },
  badges: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    padding: "12px 16px 0",
  },
  datos: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "10px 16px",
    padding: 16,
  },
  datoLabel: {
    color: lightGray,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  datoValor: {
    color: blue900,
    fontSize: 14,
    wordBreak: "break-word",
  },
  acciones: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: "0 16px 16px",
  },
  verMas: {
    width: "100%",
  },
}));

const DASH = "—";

interface GuardiaVehicleCardMobileProps {
  row: VehicleInsideRow;
  onVerMas: () => void;
  onSalida: () => void;
  onEditDock?: (target: EditDockTarget) => void;
}

/** Tarjeta de una unidad dentro del complejo. Espeja las columnas de la tabla de escritorio. */
const GuardiaVehicleCardMobile = ({
  row,
  onVerMas,
  onSalida,
  onEditDock,
}: GuardiaVehicleCardMobileProps) => {
  const classes = useStyles();
  const { day, time } = formatGuardiaFecha(row.fecha);
  const nombre = driverName(row.driver);
  const canEditDock = Boolean(
    row.eventId && getEntryTransportAuthorization({ event: row.event } as any)
  );

  const asociadas = row.associatedVehicles ?? [];

  // Los mismos chips con punto de estado que la tabla de escritorio, para no perder el dato en
  // mobile: verde dentro, amarillo fuera.
  const datos: Array<{ label: string; valor: React.ReactNode }> = [
    { label: "Fecha y hora de entrada", valor: `${day} ${time}`.trim() || DASH },
    {
      label: "Unidad asociada",
      valor:
        asociadas.length > 0 ? (
          <div className="gv-asociadas gv-asociadas-mobile">
            {asociadas.map((unit, index) => (
              <AsociadaChip key={unit.plate ?? index} unit={unit} />
            ))}
          </div>
        ) : (
          DASH
        ),
    },
    {
      label: "Ingresante / chofer",
      valor: nombre ? `${nombre} · DNI ${row.driver?.dni ?? ""}`.trim() : DASH,
    },
    { label: "Empresa de transporte", valor: row.transportCompanyName || DASH },
    {
      label: "Dock",
      valor: (
        <span className="gt-dock-cell">
          <span>{row.dock ? String(row.dock) : DASH}</span>
          {canEditDock && (
            <Tooltip title="Editar dock asignado">
              <IconButton
                className="dock-edit-button"
                aria-label={`Editar dock asignado de ${row.plate}`}
                size="small"
                onClick={() =>
                  onEditDock?.({
                    eventId: row.eventId,
                    nombre: nombre || "Transporte",
                    transportCompanyName: row.transportCompanyName,
                    patente: row.plate,
                    fecha: row.fecha,
                    dock: row.dock,
                  })
                }
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </span>
      ),
    },
  ];

  return (
    <div className={!row.driverInside ? `${classes.root} ${classes.rootAlert}` : classes.root}>
      <div className={classes.header}>
        <VehicleTypeIcon role={row.role} vehicleTypeName={row.vehicleTypeName} />
        <div>
          <div className={classes.plate}>{row.plate || "Sin patente"}</div>
          <div className={classes.tipo}>{tipoLabel(row)}</div>
        </div>
      </div>

      {/* Todo lo que se lista está dentro, así que sólo se marca la excepción. */}
      {!row.driverInside && (
        <div className={classes.badges}>
          <Badge label="Sin chofer" variant="yellow" />
        </div>
      )}

      <div className={classes.datos}>
        {datos.map(({ label, valor }) => (
          <div key={label}>
            <div className={classes.datoLabel}>{label}</div>
            <div className={classes.datoValor}>{valor}</div>
          </div>
        ))}
      </div>

      <div className={classes.acciones}>
        <Button variant="outline" onClick={onVerMas} className={classes.verMas}>
          Ver más
        </Button>
        <Button variant="outline-error" onClick={onSalida} className={classes.verMas}>
          <ExitToAppIcon fontSize="small" />
          &nbsp;Salida
        </Button>
      </div>
    </div>
  );
};

export default GuardiaVehicleCardMobile;
